import type { PMIssue, PMIssueDependency } from "@/domains/pm/types";
import { barRect, type GanttScale } from "@/domains/pm/utils/ganttScale";
import {
  arrowHead,
  edgePath,
  violatesConstraint,
  type BarBox,
} from "@/domains/pm/utils/ganttDependencies";
import { GANTT_BAR_HEIGHT, GANTT_ROW_HEIGHT } from "./ganttConstants";

/**
 * The arrows between bars.
 *
 * One absolutely-positioned SVG over the whole chart rather than an element
 * per row: an arrow crosses rows by definition, so it cannot live in either of
 * the rows it joins.
 *
 * The layer itself is click-through; only the strokes take a pointer, so a
 * link can be selected without the arrows stealing drags meant for the bars
 * underneath.
 */
export function GanttDependencyLayer({
  dependencies,
  issuesById,
  rowTops,
  scale,
  width,
  height,
  selectedId,
  onSelect,
  isStaged,
}: {
  dependencies: PMIssueDependency[];
  issuesById: Map<number, PMIssue>;
  /** y of each issue's row within the chart body. */
  rowTops: ReadonlyMap<number, number>;
  scale: GanttScale;
  width: number;
  height: number;
  selectedId: number | null;
  onSelect: (dependency: PMIssueDependency | null) => void;
  /** Drawn provisionally: staged, not yet confirmed. */
  isStaged?: (dependencyId: number) => boolean;
}) {
  const boxFor = (issue: PMIssue): BarBox | null => {
    const top = rowTops.get(issue.id);
    if (top == null) return null;
    const start = issue.start_date ?? issue.due_date;
    const due = issue.due_date ?? issue.start_date;
    if (!start || !due) return null;
    const { left, width: w } = barRect(scale, start, due);
    return {
      left,
      width: w,
      top: top + (GANTT_ROW_HEIGHT - GANTT_BAR_HEIGHT) / 2,
      height: GANTT_BAR_HEIGHT,
    };
  };

  return (
    <svg
      className="pointer-events-none absolute left-0 top-0 z-20 overflow-visible"
      width={width}
      height={height}
      aria-hidden="true"
    >
      {dependencies.map((dep) => {
        const predecessor = issuesById.get(dep.predecessor_issue_id);
        const successor = issuesById.get(dep.successor_issue_id);
        // An edge with an end off the loaded page draws nothing here; the bar
        // itself carries a stub badge so the link is not silently invisible.
        if (!predecessor || !successor) return null;
        const from = boxFor(predecessor);
        const to = boxFor(successor);
        if (!from || !to) return null;

        const violated = violatesConstraint(dep, predecessor, successor);
        const selected = selectedId === dep.id;
        const pending = isStaged?.(dep.id) ?? false;
        // Amber for staged, matching the confirm bar and the moved bars, so
        // one colour means "not saved yet" everywhere in the view.
        const colour = pending
          ? "#d97706"
          : violated
            ? "#ef4129"
            : selected
              ? "#1976d2"
              : "#9ca3af";
        const head = arrowHead(to, dep.dep_type);
        const d = edgePath(from, to, dep.dep_type);

        return (
          <g key={dep.id}>
            {/* A wide invisible stroke under the visible one: a 1px line is
                not a target anybody can hit with a mouse. */}
            <path
              d={d}
              fill="none"
              stroke="transparent"
              strokeWidth={10}
              className="pointer-events-auto cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(selected ? null : dep);
              }}
            />
            <path
              d={d}
              fill="none"
              stroke={colour}
              strokeWidth={selected ? 2 : 1.25}
              strokeDasharray={dep.hardness === "soft" ? "4 3" : undefined}
            />
            <polygon
              points="0,-3.5 6,0 0,3.5"
              fill={colour}
              transform={`translate(${head.x - (head.rotate === 0 ? 6 : -6)} ${head.y}) rotate(${head.rotate})`}
            />
          </g>
        );
      })}
    </svg>
  );
}
